import { useNavigate } from 'react-router-dom';
import { Building2, Users, TrendingUp } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { CompanyScatterData } from '@/hooks/usePlatformAnalytics';

interface TopProspectsTableProps {
  data: CompanyScatterData[];
  maxRows?: number;
}

export default function TopProspectsTable({ data, maxRows = 10 }: TopProspectsTableProps) {
  const navigate = useNavigate();

  // Get badge color for AI adoption stage
  const getAiStageBadgeColor = (stage: string | null) => {
    switch (stage) {
      case 'Not Started':
        return 'bg-gray-500/10 text-gray-500 dark:bg-gray-400/10 dark:text-gray-400';
      case 'Exploring':
        return 'bg-blue-500/10 text-blue-500 dark:bg-blue-400/10 dark:text-blue-400';
      case 'Pilot':
        return 'bg-yellow-500/10 text-yellow-600 dark:bg-yellow-400/10 dark:text-yellow-400';
      case 'Implementing':
        return 'bg-orange-500/10 text-orange-600 dark:bg-orange-400/10 dark:text-orange-400';
      case 'Mature':
        return 'bg-green-500/10 text-green-600 dark:bg-green-400/10 dark:text-green-400';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  // Get score color
  const getScoreColor = (score: number) => {
    if (score >= 70) return 'text-green-600 dark:text-green-400';
    if (score >= 40) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-gray-600 dark:text-gray-400';
  };

  // Format revenue range for display
  const formatRevenueRange = (range: string) => {
    return range.replace('-', ' – ');
  };

  // Limit data to maxRows
  const topProspects = data.slice(0, maxRows);

  if (topProspects.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Top Prospects</CardTitle>
          <CardDescription>Highest-potential companies based on revenue score</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <p className="text-muted-foreground">No prospect data available</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Top Prospects</CardTitle>
        <CardDescription>
          Highest-potential companies based on revenue potential score (top {maxRows})
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[60px]">Rank</TableHead>
                <TableHead>Company</TableHead>
                <TableHead className="text-right">Revenue Score</TableHead>
                <TableHead className="text-right">Employees</TableHead>
                <TableHead>Revenue Range</TableHead>
                <TableHead>AI Stage</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {topProspects.map((prospect, index) => (
                <TableRow
                  key={prospect.id}
                  className="cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => navigate(`/companies/${prospect.id}`)}
                >
                  <TableCell className="font-medium">#{index + 1}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">{prospect.companyName}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <TrendingUp className="h-4 w-4 text-muted-foreground" />
                      <span className={`font-bold ${getScoreColor(prospect.revenueScore)}`}>
                        {prospect.revenueScore}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Users className="h-4 w-4 text-muted-foreground" />
                      <span>{prospect.employeeCount.toLocaleString()}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm">{formatRevenueRange(prospect.revenueRange)}</span>
                  </TableCell>
                  <TableCell>
                    {prospect.aiAdoptionStage ? (
                      <Badge className={getAiStageBadgeColor(prospect.aiAdoptionStage)}>
                        {prospect.aiAdoptionStage}
                      </Badge>
                    ) : (
                      <span className="text-muted-foreground text-sm">-</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
